import { resolveSlug } from "@/features/public/slug";
import { webStorage } from "@/lib/webStorage";

/**
 * Where a sign-in lands, when the person came from somewhere.
 *
 * A stranger on somebody's page — a friend's link, opened from a chat —
 * pressed "Start your own timelapse", signed in, and was put on the
 * diary: the friend's page, with the Follow that was the whole point of
 * the link, gone. The phone opens a link on the page it names; the web
 * does the same for the moment after sign-in. The page is remembered
 * when the door is pressed from it and taken on the first sign-in that
 * succeeds — a failed attempt keeps it for the next — in the tab's own
 * storage, which is where the X flow already keeps what survives its
 * redirect. Only a path shaped like a public page's — one segment, read
 * by the rule that routes it — is ever remembered or followed, so nothing
 * written into storage can send anybody off this origin; the app's own
 * first segments pass the shape too, harmlessly, being this origin's.
 */
export const AFTER_SIGN_IN_KEY = "selfeed.web.afterSignIn";

/** A path shaped like a profile's or a shared film's own, and nothing else. */
export function isPublicPage(path: string): boolean {
  const match = /^\/([^/?#]+)$/.exec(path);
  return match?.[1] !== undefined && resolveSlug(match[1]) !== null;
}

export function rememberDestination(path: string): void {
  if (!isPublicPage(path)) return;
  webStorage("session")?.setItem(AFTER_SIGN_IN_KEY, path);
}

/** The remembered page, once; the diary's path when there is none. */
export function takeDestination(): string {
  const stash = webStorage("session");
  const path = stash?.getItem(AFTER_SIGN_IN_KEY) ?? null;
  stash?.removeItem(AFTER_SIGN_IN_KEY);
  return path !== null && isPublicPage(path) ? path : "/dashboard";
}
